import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { formatMoney, formatDate } from '../core/format.js';
import { icon, unitIcon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { showFormError, clearFormError, fieldFromApiError } from '../core/formError.js';
import { attachMembersPanel } from '../components/MembersPanel.js';
import { attachDocumentsPanel } from '../components/DocumentsPanel.js';
import { attachUnitDocumentsPanel } from '../components/UnitDocumentsPanel.js';
import { decodeAmenities } from '../core/propertyCatalogs.js';
import { attachUnitExternalOwnersPanel } from '../components/UnitExternalOwnersPanel.js';
import { attachUnitMetersPanel } from '../components/UnitMetersPanel.js';
import { unitCardHtml, unitFormHtml, unitDraftFromRow, emptyUnitDraft, wireUnitFormApartmentOnly, wireUnitCurrencySuffix } from '../components/UnitCard.js';
import {
  wireEnergyRatingInputs, wireAmenitiesChips, wireEnergyCertVerify, wireFurnishedRadio,
  collectBasicsPayload, BASICS_API_FIELDS, wireAddressFields,
} from '../components/PropertyBasicsFields.js';

export class PropertyView extends Component {
  constructor({ params, query } = { params: {} }) {
    super();
    this.id = params.id;
    // Active tab, deep-linked via ?tab= so a refresh stays put. Valid keys
    // below; anything else falls back to 'units'.
    this.state = {
      tab: PropertyView._normalizeTab(query && query.tab),
      // Tab badge counts. units derives from state.units; the rest are
      // seeded by _load and kept live by panel onCount/onChange callbacks.
      counts: { documents: null, photos: null, members: null },
      property: null,
      units: null,
      // Tenancies covering this property's units (Bérlők tab). null =
      // loading, [] = none; loaded by _loadTenancies alongside _load.
      tenancies: null,
      tenanciesError: null,
      tenancyUnread: {},
      // Maintenance requests across the property (Karbantartás tab, only
      // when the plan has the maintenance feature). null = loading.
      maintenance: null,
      maintenanceError: null,
      meters: {},
      readings: {},
      error: null,
      addingUnit: false,
      editingProperty: false,
      editingUnitId: null,
      addingMeterUnitId: null,
      editingMeterId: null,
      replacingMeterId: null,
      addingReadingMeterId: null,
      editingReadingId: null,
      publishingOpen: false,
      publishingBusy: false,
      // v5 unit-card state: working-copy keyed by unit id (expanded ⇒
      // present, collapsed ⇒ absent), plus the in-flight draft for the
      // "+ Add unit" form so it survives re-render.
      unitDrafts: {},
      newUnit: emptyUnitDraft(),
    };
    this._unitPanels = {};
    this._tabPanels = [];
    this._unitBusy = false;
  }

  _destroyTabPanels() {
    for (const p of this._tabPanels) { try { p?.destroy?.(); } catch {} }
    this._tabPanels = [];
  }

  _publicUrl(slug) {
    return `${window.location.origin}/#/p/${slug}`;
  }

  static METER_KINDS = ['electricity', 'gas', 'water', 'heat', 'fee', 'other'];
  static READING_SOURCES = ['manual', 'photo', 'estimate'];

  // Detail-page tabs. 'publishing' is only shown when the marketplace is
  // enabled, but it stays a valid deep-link target regardless.
  static TABS = ['units', 'tenancies', 'maintenance', 'documents', 'photos', 'members', 'publishing'];
  static _normalizeTab(tab) {
    return PropertyView.TABS.includes(tab) ? tab : 'units';
  }

  // Switch tabs in place: update state + the URL (?tab=) without going
  // through the router (replaceState doesn't fire hashchange), so the page
  // doesn't remount/refetch. A refresh re-reads ?tab= in the constructor.
  _setTab(tab) {
    const next = PropertyView._normalizeTab(tab);
    if (next === this.state.tab) return;
    if (typeof window !== 'undefined' && window.history) {
      try {
        const url = new URL(window.location.href);
        url.hash = `#/properties/${encodeURIComponent(this.id)}?tab=${encodeURIComponent(next)}`;
        window.history.replaceState(null, '', url.toString());
      } catch { /* jsdom may throw on replaceState */ }
    }
    this.setState({ tab: next });
  }

  // Update a tab's badge count in place (no setState — that would remount
  // the active panel). Called by panel onCount/onChange callbacks.
  _setTabCount(key, n) {
    if (key in this.state.counts) this.state.counts[key] = n;
    const el = this.el && this.el.querySelector(`[data-tab-count="${key}"]`);
    if (el) el.textContent = (n == null ? '' : String(n));
  }

  template() {
    const p = this.state.property;
    return `
      <section class="stack">
        <a href="#/properties" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('property.back'))}</span></a>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${p === null ? `<div class="muted">${esc(t('property.loading'))}</div>` : `
          ${p.deleted_at ? this._archivedBannerHtml(p) : ''}
          ${this.state.editingProperty ? this._propertyFormHtml(p) : this._propertyCardHtml(p)}
          ${this._tabsHtml()}
          <div class="tab-panel" data-tab-panel="${esc(this.state.tab)}">
            ${this._tabContentHtml()}
          </div>
        `}
      </section>
    `;
  }

  // Tab bar pinned under the property header. Each tab carries a live count
  // badge (units derives from state; others from state.counts, kept fresh by
  // the panels). Publikálás appears only when the marketplace is enabled.
  _tabsHtml() {
    const tab = this.state.tab;
    const unitCount = Array.isArray(this.state.units) ? this.state.units.length : null;
    const counts = this.state.counts;
    const tenancyCount = Array.isArray(this.state.tenancies) ? this.state.tenancies.length : null;
    const defs = [
      ['units', t('property.tabs.units') || 'Egységek', unitCount],
      ['tenancies', t('property.tabs.tenancies') || 'Bérlők', tenancyCount],
    ];
    if (auth.hasFeature('maintenance')) {
      const openMaint = Array.isArray(this.state.maintenance)
        ? this.state.maintenance.filter((m) => m.status === 'OPEN' || m.status === 'IN_PROGRESS').length
        : null;
      defs.push(['maintenance', t('property.tabs.maintenance') || 'Karbantartás', openMaint]);
    }
    defs.push(
      ['documents', t('property.tabs.documents') || 'Dokumentumok', counts.documents],
      ['photos', t('property.tabs.photos') || 'Képek', counts.photos],
      ['members', t('property.tabs.members') || 'Hozzáférések', counts.members],
    );
    if (auth.marketplaceEnabled) defs.push(['publishing', t('property.tabs.publishing') || 'Publikálás', null]);
    return `
      <div class="tabs" role="tablist">
        ${defs.map(([key, label, count]) => `
          <button type="button" class="tab${tab === key ? ' is-active' : ''}" role="tab"
                  aria-selected="${tab === key ? 'true' : 'false'}" data-tab="${key}">
            <span>${esc(label)}</span>
            <span class="tab-count mono" data-tab-count="${key}">${count == null ? '' : esc(String(count))}</span>
          </button>`).join('')}
      </div>`;
  }

  _tabContentHtml() {
    const p = this.state.property;
    switch (this.state.tab) {
      case 'tenancies':
        return this._tenanciesTabHtml();
      case 'maintenance':
        return auth.hasFeature('maintenance') ? this._maintenanceTabHtml() : this._unitsTabHtml();
      case 'documents':
        return '<div data-documents-slot></div>';
      case 'photos':
        return '<div data-photos-slot></div>';
      case 'members':
        return '<div data-members-slot></div>';
      case 'publishing':
        return auth.marketplaceEnabled ? this._publishingCardHtml(p) : this._unitsTabHtml();
      case 'units':
      default:
        return this._unitsTabHtml();
    }
  }

  _unitsTabHtml() {
    return `
      ${this._unitsEmpty() ? `
        <div class="warn-banner">
          <strong>${esc(t('property.no_units_warning_strong'))}</strong>
          ${esc(t('property.no_units_warning'))}
        </div>` : ''}
      <div class="section-head">
        <div class="section-head-titles">
          <div class="section-head-eyebrow">${esc(t('property.units_title'))}</div>
          <div class="section-head-count mono">${esc(String(Array.isArray(this.state.units) ? this.state.units.length : 0))} ${esc((t(Array.isArray(this.state.units) && this.state.units.length === 1 ? 'properties.row_meta.unit' : 'properties.row_meta.units') || 'units'))}</div>
        </div>
      </div>
      ${this._unitsHtml()}
      <a class="add-tile" href="#/properties/${encodeURIComponent(this.id)}/units/new">
        <span class="add-tile-plus">+</span>
        <span class="add-tile-label">${esc(t('property.unit_form.add') || t('property.unit_button'))}</span>
      </a>
    `;
  }

  // ---- Bérlők tab: tenancies covering this property's units ----

  static TENANCY_STATUS_GROUPS = ['ACTIVE', 'DRAFT', 'ENDED'];

  _tenanciesTabHtml() {
    const list = this.state.tenancies;
    const moveInHref = `#/tenancies/new?property=${encodeURIComponent(this.id)}`;
    if (this.state.tenanciesError) {
      return `<div class="error-banner">${esc(this.state.tenanciesError)}</div>`;
    }
    if (list === null) return `<div class="muted">${esc(t('tenancies.loading'))}</div>`;
    const cta = `
      <a class="btn btn-primary" href="${moveInHref}">
        ${icon('user-plus', { size: 'sm' })}<span>${esc(t('move_in.entry'))}</span>
      </a>`;
    if (list.length === 0) {
      return `
        <div class="empty stack-2">
          <span>${esc(t('property.tenancies.empty'))}</span>
          <div class="row" style="justify-content:center">${cta}</div>
        </div>`;
    }
    const groups = new Map(PropertyView.TENANCY_STATUS_GROUPS.map((s) => [s, []]));
    for (const c of list) {
      const s = String(c.status || '');
      if (groups.has(s)) groups.get(s).push(c);
    }
    const sections = PropertyView.TENANCY_STATUS_GROUPS
      .filter((s) => groups.get(s).length > 0)
      .map((s) => `
        <div class="tenancies-group">
          <div class="tenancies-group-header">
            <span class="group-title">${esc(t(`tenancies.groups.${s}`))}</span>
            <span class="faint">· ${groups.get(s).length}</span>
          </div>
          <div class="list">${groups.get(s).map((c) => this._tenancyRowHtml(c)).join('')}</div>
        </div>`)
      .join('');
    return `
      ${this._tenancyStatsHtml(list)}
      <div class="section-head">
        <div class="section-head-titles">
          <div class="section-head-eyebrow">${esc(t('property.tenancies.title'))}</div>
          <div class="section-head-count mono">${esc(String(list.length))}</div>
        </div>
        ${cta}
      </div>
      ${sections}
      ${this._vacantUnitsHintHtml(moveInHref)}
    `;
  }

  // Monthly income (rent + közös of this property's units on ACTIVE
  // tenancies) and occupancy — the "how is this building doing" glance.
  _tenancyStatsHtml(list) {
    const currency = (this.state.property?.currency || 'HUF').toUpperCase();
    let monthly = 0;
    for (const c of list) {
      if (c.status !== 'ACTIVE') continue;
      for (const u of (c.units || [])) {
        if (String(u.property_id) !== String(this.id)) continue;
        monthly += (Number(u.rent_amount) || 0) + (Number(u.kozos_koltseg) || 0);
      }
    }
    const units = Array.isArray(this.state.units) ? this.state.units : [];
    const occupied = units.filter((u) => u.availability_status === 'occupied').length;
    return `
      <div class="stats" data-tenancy-stats>
        <div class="stat acc">
          <div class="stat-k">${esc(t('property.tenancies.monthly_income'))}</div>
          <div class="stat-v">${monthly ? esc(formatMoney(monthly, currency)) : '—'}</div>
          ${monthly ? `<div class="stat-trend">${esc((t('public.per_month_suffix') || '/mo').trim().toUpperCase())}</div>` : ''}
        </div>
        <div class="stat">
          <div class="stat-k">${esc(t('property.tenancies.occupancy'))}</div>
          <div class="stat-v">${units.length ? `${esc(String(occupied))} / ${esc(String(units.length))}` : '—'}</div>
        </div>
      </div>`;
  }

  // One tenancy row — TenanciesView's list-item pattern, scoped to this
  // property: unit identifiers instead of property names, plus the row's
  // monthly total for the units that belong here.
  _tenancyRowHtml(c) {
    const badgeClass = c.status === 'ACTIVE' ? 'badge-success'
      : c.status === 'DRAFT' ? 'badge-warning'
      : c.status === 'ENDED' ? 'badge-muted' : 'badge';
    const statusLabel = t(`tenancies.status.${c.status}`) || c.status;
    const endLabel = c.end_date ? formatDate(c.end_date) : t('common.open');
    const awaiting = !c.tenant_user_id && c.invitation_id;
    const tenantLabel = c.tenant_name || c.tenant_email || c.invitation_name || c.invitation_email || t('tenancies.tenant_fallback');
    const hereUnits = (c.units || []).filter((u) => String(u.property_id) === String(this.id));
    const unitLine = hereUnits.map((u) => u.identifier).filter(Boolean).join(' · ');
    const currency = (c.currency || this.state.property?.currency || 'HUF').toUpperCase();
    const rent = hereUnits.reduce((a, u) => a + (Number(u.rent_amount) || 0), 0);
    const kozos = hereUnits.reduce((a, u) => a + (Number(u.kozos_koltseg) || 0), 0);
    const rentLine = rent
      ? `${formatMoney(rent, currency)}${kozos ? ` + ${formatMoney(kozos, currency)}` : ''}${t('public.per_month_suffix') || '/mo'}`
      : '';
    return `
      <a class="list-item" href="#/tenancies/${esc(c.id)}">
        ${icon('file-text', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(tenantLabel)}${awaiting ? ` <span class="faint">· ${esc(t('tenancies.awaiting_tenant'))}</span>` : ''}</strong>
          <span class="faint period">
            ${unitLine ? `<span>${esc(unitLine)}</span><span>·</span>` : ''}
            <span>${esc(formatDate(c.start_date))}</span>
            ${icon('arrow-right', { size: 'sm', class: 'icon-faint' })}
            <span>${esc(endLabel)}</span>
          </span>
          ${rentLine ? `<span class="faint">${esc(rentLine)}</span>` : ''}
        </div>
        ${Number((this.state.tenancyUnread || {})[c.id] || 0) > 0
          ? `<span class="badge badge-danger">${esc(t('tenancies.unread_badge', { count: Number(this.state.tenancyUnread[c.id]) }))}</span>` : ''}
        <span class="badge ${badgeClass}">${esc(statusLabel)}</span>
      </a>`;
  }

  // ---- Karbantartás tab: maintenance requests across the property ----

  _maintenanceTabHtml() {
    const list = this.state.maintenance;
    if (this.state.maintenanceError) {
      return `<div class="error-banner">${esc(this.state.maintenanceError)}</div>`;
    }
    if (list === null) return `<div class="muted">${esc(t('tenancy.maintenance.loading'))}</div>`;
    if (list.length === 0) {
      return `<div class="empty">${esc(t('property.maintenance.empty'))}</div>`;
    }
    const isOpen = (m) => m.status === 'OPEN' || m.status === 'IN_PROGRESS';
    const open = list.filter(isOpen);
    const closed = list.filter((m) => !isOpen(m));
    const section = (titleKey, items) => items.length === 0 ? '' : `
      <div class="tenancies-group">
        <div class="tenancies-group-header">
          <span class="group-title">${esc(t(titleKey))}</span>
          <span class="faint">· ${items.length}</span>
        </div>
        <div class="list">${items.map((m) => this._maintenanceRowHtml(m)).join('')}</div>
      </div>`;
    return `
      <div class="section-head">
        <div class="section-head-titles">
          <div class="section-head-eyebrow">${esc(t('property.maintenance.title'))}</div>
          <div class="section-head-count mono">${esc(String(list.length))}</div>
        </div>
      </div>
      ${section('property.maintenance.open_group', open)}
      ${section('property.maintenance.closed_group', closed)}
    `;
  }

  // One request row linking into the owning tenancy's maintenance tab.
  _maintenanceRowHtml(m) {
    const status = String(m.status || '');
    const statusClass = status === 'OPEN' ? 'badge-warning'
      : status === 'IN_PROGRESS' ? 'primary'
      : status === 'RESOLVED' ? 'badge-success' : 'badge-muted';
    const prio = String(m.priority || '');
    const prioBadge = (prio === 'URGENT' || prio === 'HIGH')
      ? `<span class="badge danger">${esc(t('tenancy.maintenance.priority.' + prio) || prio)}</span>` : '';
    const meta = [
      m.unit_identifier || null,
      m.tenant_name || m.reporter_name || null,
      m.created_at ? formatDate(String(m.created_at).slice(0, 10)) : null,
    ].filter(Boolean).join(' · ');
    return `
      <a class="list-item" href="#/tenancies/${esc(m.tenancy_id)}?tab=maintenance">
        ${icon('wrench', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(m.title || '')}</strong>
          ${meta ? `<span class="faint">${esc(meta)}</span>` : ''}
        </div>
        ${prioBadge}
        <span class="badge ${statusClass}">${esc(t('tenancy.maintenance.status.' + status) || status)}</span>
      </a>`;
  }

  // Turn known vacancy into action: list the empty units with a jump
  // straight into the move-in wizard (property preselected).
  _vacantUnitsHintHtml(moveInHref) {
    const vacant = (Array.isArray(this.state.units) ? this.state.units : [])
      .filter((u) => u.availability_status === 'vacant')
      .map((u) => u.identifier)
      .filter(Boolean);
    if (vacant.length === 0) return '';
    return `
      <div class="row-between card" data-vacant-hint style="border-style:dashed; padding:var(--space-3) var(--space-4)">
        <span class="faint">${esc(t('property.tenancies.vacant_hint', { units: vacant.join(', ') }))}</span>
        <a class="btn btn-ghost btn-sm" href="${moveInHref}">${esc(t('property.tenancies.start_move_in'))}</a>
      </div>`;
  }

  /**
   * Banner shown above the property card whenever deleted_at is set.
   * The two actions are deliberately separated: "Restore" is a one-click
   * undo, while "Delete permanently" is a destructive path with a
   * confirm dialog and a server-side safety check (refuses if any unit
   * is on a tenancy of any status).
   */
  _archivedBannerHtml(p) {
    return `
      <div class="warn-banner stack-2" data-role="archived-banner">
        <strong>${esc(t('property.archived_title'))}</strong>
        <span class="faint">${esc(t('property.archived_help', { date: p.deleted_at }))}</span>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap">
          <button class="btn btn-primary" type="button" data-action="restore-property">
            ${icon('arrow-left', { size: 'sm' })}<span>${esc(t('property.restore'))}</span>
          </button>
          <button class="btn btn-ghost" type="button" data-action="delete-property-permanent">
            ${icon('trash', { size: 'sm' })}<span>${esc(t('property.delete_permanently'))}</span>
          </button>
        </div>
      </div>
    `;
  }

  // Flow-v5 panel chrome — same panel-head / panel-body pattern as the
  // dashboard, properties list and wizard so this page stops looking
  // like a one-off. Identity goes in the head; address, stats and
  // amenities live in the body. Actions sit in the head's status slot
  // (View public + Sheet + Edit), with the listed/draft/archived
  // badge shown next to them.
  _propertyCardHtml(p) {
    const addrFull = [p.address_line1, [p.postal_code, p.city].filter(Boolean).join(' '), p.country].filter(Boolean).join(', ');
    const energy = (p.energy_rating || '').toString().toUpperCase();
    const amenities = decodeAmenities(p.amenities);
    const heroLabel = String(t('property.hero_label') || 'Property');
    const title = p.name || p.address_line1 || heroLabel;
    const units = Array.isArray(this.state.units) ? this.state.units : [];
    const unitCount = units.length;
    const sumRent = units.reduce((a, u) => a + (Number(u.asking_rent) || 0), 0);
    const sumKozos = units.reduce((a, u) => a + (Number(u.kozos_koltseg) || 0), 0);
    const currency = (p.currency || 'HUF').toUpperCase();
    const createdAt = p.created_at ? String(p.created_at).slice(0, 10) : '';
    const slug = p.public_slug || '';
    const listed = !!Number(p.is_published);
    const archived = !!p.deleted_at;
    const amenitiesHtml = amenities.length
      ? `<div class="chip-grid" style="margin-top: 18px;">${amenities.map((k) =>
          `<span class="chip chip-on" aria-pressed="true"><span>${esc(t('property.amenity.' + k) || k)}</span></span>`
        ).join('')}</div>`
      : '';
    // Archived takes precedence; "Vázlat" only makes sense when the
    // marketplace is enabled — otherwise there's no concept of
    // published-vs-draft and the badge is just noise.
    const statusBadge = archived
      ? `<span class="badge">${esc(t('properties.archived_badge') || 'Archived')}</span>`
      : (auth.marketplaceEnabled
        ? (listed
          ? `<span class="badge primary"><span class="dot-anim"></span>${esc(t('property.hero_status_listed') || 'Listed')}</span>`
          : `<span class="badge">${esc(t('property.hero_status_draft') || 'Draft')}</span>`)
        : '');
    const eyebrowParts = [heroLabel.toUpperCase()];
    if (createdAt) eyebrowParts.push(`${(t('property.hero_created') || 'created').toUpperCase()} ${createdAt}`);
    const eyebrow = eyebrowParts.join(' · ');
    const statValue = (v) => v === '' || v == null ? '—' : v;
    return `
      <div class="panel" data-property-header>
        <div class="panel-head">
          <div class="panel-titles">
            <div class="panel-title-eyebrow">${esc(eyebrow)}</div>
            <h2 class="panel-title">${esc(title)}${unitCount ? ` <span class="panel-title-acc">· ${esc(String(unitCount))} ${esc((t(unitCount === 1 ? 'properties.row_meta.unit' : 'properties.row_meta.units') || 'units'))}</span>` : ''}</h2>
          </div>
          ${statusBadge ? `<div class="panel-status">${statusBadge}</div>` : ''}
        </div>
        <div class="panel-body">
          <div class="row" style="justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 16px;">
            ${addrFull ? `<div class="panel-lede" style="margin-bottom: 0;">${esc(addrFull)}</div>` : '<div></div>'}
            <div class="row" style="gap: 8px; flex-wrap: wrap;">
              ${listed && slug ? `<a class="btn btn-sm btn-ghost" href="#/p/${esc(slug)}" target="_blank" rel="noopener">${icon('arrow-up-right', { size: 'sm' })}<span>${esc(t('property.hero_view_public') || 'View public')}</span></a>` : ''}
              <a class="btn-mono" href="#/properties/${esc(this.id)}/sheet" target="_blank" rel="noopener">${esc(t('property.hero_sheet') || 'Sheet')}</a>
              <button class="btn-mono" type="button" data-action="edit-property">${esc(t('common.edit'))}</button>
            </div>
          </div>
          <div class="stats" data-property-stats>
            <div class="stat acc"><div class="stat-k">${esc(t('property.stats.units') || 'Units')}</div><div class="stat-v">${esc(String(unitCount))}</div>${sumRent ? `<div class="stat-trend">${esc(formatMoney(sumRent, currency))} ${esc(t('public.per_month_suffix') || '/mo').trim()}</div>` : ''}</div>
            <div class="stat"><div class="stat-k">${esc(t('property.stats.kozos') || 'Közös költség')}</div><div class="stat-v">${sumKozos ? esc(formatMoney(sumKozos, currency)) : '—'}</div>${sumKozos ? `<div class="stat-trend">${esc((t('public.per_month_suffix') || '/mo').trim().toUpperCase())}</div>` : ''}</div>
            <div class="stat"><div class="stat-k">${esc(t('property.stats.energy') || 'Energy rating')}</div><div class="stat-v">${esc(statValue(energy))}</div>${p.energy_certificate_expires_at ? `<div class="stat-trend">${esc((t('property.stats.energy_expires') || 'expires').toUpperCase())} ${esc(String(p.energy_certificate_expires_at).slice(0, 10))}</div>` : ''}</div>
          </div>
          ${amenitiesHtml}
        </div>
      </div>
    `;
  }

  _propertyFormHtml(p) {
    const currency = (p.currency || 'HUF').toUpperCase();
    return `
      <form class="card stack" data-property-form>
        <h3>${esc(t('property.edit_property_form_title'))}</h3>
        <div data-form-error></div>
        <div class="field">
          <label>${esc(t('property.name_label') || 'Ingatlan neve')}</label>
          <input class="input" name="name" maxlength="191" value="${esc(p.name || '')}" />
          <span class="faint">${esc(t('property.name_help') || 'Létrehozáskor a fő egység címéből származik; szabadon átírható, a cím változása nem írja felül.')}</span>
        </div>
        ${(() => {
          // The address belongs to the property's primary unit now (the
          // property only caches it). Show it read-only here with a pointer
          // to edit it on the unit, instead of a second editable copy that
          // would drift from the unit. Amenities (migration 0084) follow
          // the same pattern — edited on each unit's card.
          const addr = [p.address_line1, [p.postal_code, p.city].filter(Boolean).join(' '), p.country]
            .filter(Boolean).join(', ');
          return `
            <div class="note">
              <span class="note-l">${esc(t('properties.address') || 'Cím')}</span>
              ${addr ? esc(addr) : esc(t('property.address_via_unit_empty') || '—')}
              <div class="faint" style="margin-top:6px;">${esc(t('property.address_via_unit_hint') || 'A cím az egységnél szerkeszthető — a fő egység címét tükrözi.')}</div>
            </div>`;
        })()}
        <div class="field-row">
          <div class="field"><label>${esc(t('properties.currency_iso'))}</label>
            <input class="input" name="currency" maxlength="3" pattern="[A-Za-z]{3}" placeholder="HUF" value="${esc(currency)}" /></div>
          <div class="field"><label>${esc(t('property.default_deposit_months'))} <span class="faint">(${esc(t('common.optional'))})</span></label>
            <input class="input" name="default_deposit_months" type="number" min="0" max="24" step="1" inputmode="numeric" placeholder="2" value="${esc(p.default_deposit_months ?? '')}" />
            <span class="faint">${esc(t('property.default_deposit_months_help'))}</span></div>
        </div>
        <div class="row" style="justify-content:space-between">
          <button type="button" class="btn btn-ghost" data-action="delete-property">${icon('trash', { size: 'sm' })}<span>${esc(t('property.archive'))}</span></button>
          <div class="row" style="gap:var(--space-3)">
            <button type="button" class="btn btn-ghost" data-action="cancel-property">${esc(t('common.cancel'))}</button>
            <button type="submit" class="btn btn-primary">${esc(t('common.save'))}</button>
          </div>
        </div>
      </form>
    `;
  }

  _publishingCardHtml(p) {
    const isPub = !!Number(p.is_published);
    const slug = p.public_slug || '';
    const url = slug ? this._publicUrl(slug) : '';
    const open = this.state.publishingOpen;
    const statusLabel = isPub ? t('property.publishing.public') : t('property.publishing.not_public');
    return `
      <div class="card stack" data-publishing-card>
        <div class="row-between">
          <div class="stack-2">
            <div class="eyebrow eyebrow-accent">${esc(t('property.publishing.title'))}</div>
            ${isPub
              ? `<span class="faint"><a href="#/p/${esc(slug)}" target="_blank" rel="noopener">${esc(url)}</a></span>`
              : `<span class="faint">${esc(t('property.publishing.not_public'))}</span>`}
          </div>
          <div class="row" style="gap:var(--space-2); align-items:center;">
            <span class="badge ${isPub ? 'primary' : 'muted'}">${esc(statusLabel)}</span>
            <button class="btn btn-ghost btn-sm" data-action="toggle-publishing">${esc(t(open ? 'common.close' : 'common.edit'))}</button>
          </div>
        </div>
        ${open ? this._publishingFormHtml(p) : ''}
        ${isPub ? `
          <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
            <a class="btn btn-ghost btn-sm" href="#/p/${esc(slug)}" target="_blank" rel="noopener">${icon('external-link', { size: 'sm' })}<span>${esc(t('property.publishing.view_public'))}</span></a>
            <a class="btn btn-ghost btn-sm" href="#/properties/${esc(this.id)}/sheet" target="_blank" rel="noopener">${icon('printer', { size: 'sm' })}<span>${esc(t('property.publishing.print_sheet'))}</span></a>
            <button class="btn btn-ghost btn-sm" data-action="copy-public-url" data-url="${esc(url)}">${icon('copy', { size: 'sm' })}<span>${esc(t('property.publishing.copy_url'))}</span></button>
          </div>` : ''}
      </div>
    `;
  }

  _publishingFormHtml(p) {
    const isPub = !!Number(p.is_published);
    return `
      <form class="stack" data-publishing-form>
        <div data-form-error></div>
        <label class="row" style="gap:var(--space-3); align-items:center">
          <input type="checkbox" name="is_published" ${isPub ? 'checked' : ''} />
          <span>${esc(t('property.publishing.publish_label'))}</span>
        </label>
        <div class="field">
          <label>${esc(t('property.publishing.public_title'))}</label>
          <input class="input" name="public_title" maxlength="190" value="${esc(p.public_title || '')}" placeholder="${esc(t('property.publishing.public_title_placeholder'))}" />
          <div class="faint">${esc(t('property.publishing.public_title_help'))}</div>
        </div>
        <div class="field">
          <label>${esc(t('property.publishing.description'))}</label>
          <textarea class="input" name="public_description" rows="3" placeholder="${esc(t('property.publishing.description_placeholder'))}">${esc(p.public_description || '')}</textarea>
        </div>
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('property.publishing.contact_email'))}</label>
            <input class="input" name="public_contact_email" type="email" value="${esc(p.public_contact_email || '')}" placeholder="rentals@example.com" />
          </div>
          <div class="field grow">
            <label>${esc(t('property.publishing.contact_phone'))}</label>
            <input class="input" name="public_contact_phone" value="${esc(p.public_contact_phone || '')}" placeholder="+36 30 …" />
          </div>
        </div>
        <div class="faint">${esc(t('property.publishing.footnote'))}</div>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="cancel-publishing">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.publishingBusy ? 'disabled' : ''}>${esc(t('common.save'))}</button>
        </div>
      </form>
    `;
  }

  _unitsEmpty() {
    return Array.isArray(this.state.units) && this.state.units.length === 0;
  }

  _unitsHtml() {
    if (this.state.units === null) return `<div class="muted">${esc(t('property.loading_units'))}</div>`;
    if (this.state.units.length === 0) return `<div class="empty">${esc(t('property.no_units_yet'))}</div>`;
    const propCurrency = (this.state.property?.currency || 'HUF').toUpperCase();
    // Cards are navigational now — a click opens the unit's own page where
    // details show read-only and editing + meters/docs/owners live. Each
    // card formats money in its own currency (unit override wins,
    // property default falls back) so a EUR commercial unit and a HUF
    // flat on the same property render correctly side by side.
    return `<div class="stack">${this.state.units.map((u, i) =>
      unitCardHtml({
        unit: u,
        index: i,
        currency: (u.currency ? String(u.currency) : propCurrency).toUpperCase(),
        href: `#/properties/${encodeURIComponent(this.id)}/units/${encodeURIComponent(u.id)}`,
      })
    ).join('')}</div>`;
  }

  /**
   * Per-unit slots for the new tulajdoni-lap + owners panels. The
   * UnitDocumentsPanel / UnitExternalOwnersPanel components mount into
   * these (data-unit-documents-slot / data-unit-external-owners-slot
   * keyed on the unit id) so each unit owns its deed and the owners
   * extracted from it.
   */
  _unitDeedAndOwnersBlockHtml(u) {
    return `
      <div class="stack" style="padding:0 var(--space-3) var(--space-3) var(--space-6)">
        <div data-unit-documents-slot="${esc(u.id)}"></div>
        <div data-unit-external-owners-slot="${esc(u.id)}"></div>
      </div>
    `;
  }

  _metersBlockHtml(u) {
    const meters = this.state.meters[u.id];
    const adding = this.state.addingMeterUnitId === u.id;
    const addLabel = `+ ${String(t('property.meters.add_label') || 'Add meter').toUpperCase()}`;
    return `
      <div class="stack-2" style="padding:0 var(--space-3) var(--space-3) var(--space-6)">
        <span class="faint">${esc(t('property.meters.title'))}</span>
        ${this._metersListHtml(u.id, meters)}
        ${adding
          ? this._meterFormHtml(u.id)
          : `<button type="button" class="add-meter-fold" data-action="add-meter" data-unit-id="${esc(u.id)}"><span class="add-meter-fold-summary">${esc(addLabel)}</span></button>`}
      </div>
    `;
  }

  _metersListHtml(unitId, meters) {
    if (meters === undefined) return `<div class="faint">${esc(t('property.meters.loading'))}</div>`;
    if (meters.length === 0) return `<div class="faint">${esc(t('property.meters.empty'))}</div>`;
    return `<div>${meters.map((m) => {
      if (this.state.replacingMeterId === m.id) return this._replaceMeterFormHtml(m);
      if (this.state.editingMeterId === m.id) return this._meterFormHtml(unitId, m);
      return `${this._meterRowHtml(m)}${m.kind === 'fee' ? '' : this._readingsBlockHtml(m)}`;
    }).join('')}</div>`;
  }

  // Lucide icon name per meter kind. Mirrors UnitMetersPanel.KIND_ICON so
  // the two surfaces stay visually consistent.
  static METER_KIND_ICON = {
    water: 'droplet',
    electricity: 'zap',
    gas: 'flame',
    heat: 'thermometer',
    fee: 'tag',
    other: 'gauge',
  };

  _meterRowHtml(m) {
    const kind = (m.kind || 'other').toLowerCase();
    const ic = PropertyView.METER_KIND_ICON[kind] || 'gauge';
    const name = m.label || t('property.meters.kind.' + kind) || kind;
    const subBits = [];
    if (m.serial_number) subBits.push('#' + esc(m.serial_number));
    if (m.monthly_fee != null && m.monthly_fee !== '') {
      subBits.push(`${esc(this._fmtMinor(m.monthly_fee))}/mo`);
    }
    if (kind !== 'fee' && m.unit_price != null && m.unit_price !== '') {
      subBits.push(`${esc(this._fmtMinor(m.unit_price))}/${esc(m.unit_of_measure)}`);
    }
    if (m.reading_day_of_month) {
      subBits.push(esc(t('property.meters.due_day', { day: m.reading_day_of_month })));
    }
    let reading;
    if (kind === 'fee') {
      reading = `<span class="faint">${esc(t('property.meters.fixed_monthly_fee'))}</span>`;
    } else {
      const latest = (this.state.readings[m.id] || [])[0];
      reading = latest
        ? `${esc(latest.reading_value)} ${esc(m.unit_of_measure)}`
        : `<span class="faint">${esc(t('property.meters.no_readings'))}</span>`;
    }
    return `
      <div class="meter-card kind-${esc(kind)}" data-meter-id="${esc(m.id)}">
        <div class="meter-card-ic">${icon(ic, { size: 'sm' })}</div>
        <div class="meter-card-main">
          <div class="meter-card-nm">${esc(name)}</div>
          <div class="meter-card-mt">${subBits.join(' \u00b7 ') || '\u2014'}</div>
        </div>
        <div class="meter-card-rd">${reading}</div>
        <button class="btn btn-ghost btn-icon" data-action="edit-meter" data-meter-id="${esc(m.id)}" aria-label="${esc(t('property.meters.edit_aria'))}">${icon('pencil', { size: 'sm' })}</button>
      </div>
    `;
  }

  _fmtMinor(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return (n / 100).toLocaleString();
  }

  _readingsBlockHtml(m) {
    const list = this.state.readings[m.id];
    const adding = this.state.addingReadingMeterId === m.id;
    return `
      <div class="stack-2" style="padding:0 var(--space-3) var(--space-3) var(--space-6)">
        <div class="row-between">
          <span class="faint">${esc(t('property.readings.title'))}</span>
          <button class="btn btn-ghost" data-action="add-reading" data-meter-id="${esc(m.id)}">${icon('plus', { size: 'sm' })}<span>${esc(t('property.readings.add_label'))}</span></button>
        </div>
        ${adding ? this._readingFormHtml(m) : ''}
        ${this._readingsListHtml(m, list)}
      </div>
    `;
  }

  _readingsListHtml(meter, list) {
    if (list === undefined) return `<div class="faint">${esc(t('property.readings.loading'))}</div>`;
    if (list.length === 0) return `<div class="faint">${esc(t('property.readings.empty'))}</div>`;
    return `<div class="list">${list.map((r) =>
      this.state.editingReadingId === r.id ? this._readingFormHtml(meter, r) : this._readingRowHtml(meter, r)
    ).join('')}</div>`;
  }

  _readingRowHtml(meter, r) {
    const delta = r.delta !== null && r.delta !== undefined
      ? ` <span class="faint">(+${esc(r.delta)})</span>` : '';
    return `
      <div class="list-item">
        <div class="stack-2 grow">
          <strong>${esc(r.reading_value)} ${esc(meter.unit_of_measure)}${delta}</strong>
          <span class="faint">${esc(r.read_at)} · ${esc(r.source)}${r.notes ? ' · ' + esc(r.notes) : ''}</span>
        </div>
        <button class="btn btn-ghost btn-icon" data-action="edit-reading" data-reading-id="${esc(r.id)}" data-meter-id="${esc(meter.id)}" aria-label="${esc(t('property.readings.edit_aria'))}">${icon('pencil', { size: 'sm' })}</button>
      </div>
    `;
  }

  _readingFormHtml(meter, reading = null) {
    const editing = reading !== null;
    const today = new Date().toISOString().slice(0, 10);
    const initialDate = reading?.read_at || today;
    const otherReadings = (this.state.readings[meter.id] || []).filter((r) => !editing || r.id !== reading.id);
    const sameDay = otherReadings.some((r) => r.read_at === initialDate);
    return `
      <form class="card stack" data-reading-form data-meter-id="${esc(meter.id)}"${editing ? ` data-reading-id="${esc(reading.id)}"` : ''}>
        <h3>${esc(t(editing ? 'property.readings.form.edit' : 'property.readings.form.new'))}</h3>
        <div class="row" style="gap:var(--space-3)">
          <div class="field" style="max-width:160px">
            <label>${esc(t('property.readings.form.date'))}</label>
            <input class="input" name="read_at" type="date" value="${esc(initialDate)}" required />
          </div>
          <div class="field grow">
            <label>${esc(t('property.readings.form.value', { uom: meter.unit_of_measure }))}</label>
            <input class="input" name="reading_value" type="number" step="0.001" min="0" value="${esc(reading?.reading_value ?? '')}" required />
          </div>
        </div>
        ${sameDay ? `<div class="faint" data-reading-sameday-warning>${esc(t('property.readings.form.same_day_warning', { date: initialDate }))}</div>` : ''}
        <div class="field">
          <label>${esc(t('property.readings.form.source'))}</label>
          <select class="select" name="source">
            ${PropertyView.READING_SOURCES.map((s) => `<option value="${s}" ${reading?.source === s ? 'selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>`).join('')}
          </select>
        </div>
        <div class="field">
          <label>${esc(t('property.readings.form.notes'))}</label>
          <input class="input" name="notes" value="${esc(reading?.notes || '')}" />
        </div>
        <div class="row" style="justify-content:space-between">
          ${editing
            ? `<button type="button" class="btn btn-ghost" data-action="delete-reading">${icon('trash', { size: 'sm' })}<span>${esc(t('common.delete'))}</span></button>`
            : '<span></span>'}
          <div class="row" style="gap:var(--space-3)">
            <button type="button" class="btn btn-ghost" data-action="cancel-reading">${esc(t('common.cancel'))}</button>
            <button type="submit" class="btn btn-primary">${esc(t(editing ? 'common.save' : 'property.readings.form.add'))}</button>
          </div>
        </div>
      </form>
    `;
  }

  _meterFormHtml(unitId, meter = null) {
    const editing = meter !== null;
    const isFee = meter?.kind === 'fee';
    const monthlyFeeMajor = meter?.monthly_fee != null && meter?.monthly_fee !== '' ? (Number(meter.monthly_fee) / 100) : '';
    const unitPriceMajor = meter?.unit_price != null && meter?.unit_price !== '' ? (Number(meter.unit_price) / 100) : '';
    const today = new Date().toISOString().slice(0, 10);
    const formClass = editing ? 'card stack' : 'add-meter-fold open';
    const heading = editing
      ? `<h3>${esc(t('property.meters.form.edit'))}</h3>`
      : `<div class="add-meter-fold-summary">+ ${esc(String(t('property.meters.form.new') || 'New meter').toUpperCase())}</div>`;
    return `
      <form class="${formClass}" data-meter-form data-unit-id="${esc(unitId)}"${editing ? ` data-meter-id="${esc(meter.id)}"` : ''}>
        ${heading}
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('property.meters.form.kind'))}</label>
            <select class="select" name="kind" data-meter-kind>
              ${PropertyView.METER_KINDS.map((k) => `<option value="${k}" ${meter?.kind === k ? 'selected' : ''}>${k.charAt(0).toUpperCase() + k.slice(1)}</option>`).join('')}
            </select>
          </div>
          <div class="field" style="max-width:120px" data-uom-field${isFee ? ' hidden' : ''}>
            <label>${esc(t('property.meters.form.uom'))}</label>
            <input class="input" name="unit_of_measure" placeholder="${esc(t('property.meters.form.uom_placeholder'))}" value="${esc(meter?.unit_of_measure || '')}" maxlength="16" />
          </div>
        </div>
        <div class="field">
          <label>${esc(t('property.meters.form.label'))}</label>
          <input class="input" name="label" placeholder="${esc(t('property.meters.form.label_placeholder'))}" value="${esc(meter?.label || '')}" maxlength="128" />
        </div>
        <div class="field">
          <label>${esc(t('property.meters.form.serial'))}</label>
          <input class="input" name="serial_number" value="${esc(meter?.serial_number || '')}" maxlength="64" />
        </div>
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('property.meters.form.monthly_fee'))}</label>
            <input class="input" name="monthly_fee" type="number" step="0.01" min="0" placeholder="e.g. 12500" value="${esc(monthlyFeeMajor)}" />
          </div>
          <div class="field grow" data-unit-price-field${isFee ? ' hidden' : ''}>
            <label>${esc(t('property.meters.form.unit_price'))}</label>
            <input class="input" name="unit_price" type="number" step="0.01" min="0" placeholder="e.g. 65" value="${esc(unitPriceMajor)}" />
          </div>
        </div>
        <div class="field" style="max-width:180px" data-due-day-field${isFee ? ' hidden' : ''}>
          <label>${esc(t('property.meters.form.due_day'))}</label>
          <input class="input" name="reading_day_of_month" type="number" min="1" max="31" inputmode="numeric" placeholder="e.g. 5" value="${esc(meter?.reading_day_of_month ?? '')}" />
        </div>
        ${editing ? '' : `
        <div data-starting-field${isFee ? ' hidden' : ''}>
          <div class="row" style="gap:var(--space-3)">
            <div class="field" style="flex:1">
              <label>${esc(t('property.meters.form.starting_reading'))}</label>
              <input class="input" name="starting_value" type="number" step="0.001" min="0" inputmode="decimal" placeholder="${esc(t('property.meters.form.starting_reading_placeholder'))}" />
            </div>
            <div class="field" style="max-width:180px">
              <label>${esc(t('property.meters.form.reading_date'))}</label>
              <input class="input" name="starting_date" type="date" value="${esc(today)}" />
            </div>
          </div>
          <div class="field">
            <label>${esc(t('property.meters.form.starting_note'))}</label>
            <input class="input" name="starting_notes" placeholder="${esc(t('property.meters.form.starting_note_placeholder'))}" />
          </div>
        </div>`}
        <div class="row" style="justify-content:space-between">
          <div class="row" style="gap:var(--space-2)">
            ${editing
              ? `<button type="button" class="btn btn-ghost" data-action="delete-meter">${icon('trash', { size: 'sm' })}<span>${esc(t('common.delete'))}</span></button>`
              : ''}
            ${editing && !isFee
              ? `<button type="button" class="btn btn-ghost" data-action="open-replace-meter" data-meter-id="${esc(meter.id)}">${icon('arrow-right', { size: 'sm' })}<span>${esc(t('property.meters.form.replace_device'))}</span></button>`
              : ''}
          </div>
          <div class="row" style="gap:var(--space-3)">
            <button type="button" class="btn btn-ghost" data-action="cancel-meter">${esc(t('common.cancel'))}</button>
            <button type="submit" class="btn btn-primary">${esc(t(editing ? 'common.save' : 'property.meters.form.add'))}</button>
          </div>
        </div>
      </form>
    `;
  }

  _replaceMeterFormHtml(meter) {
    const today = new Date().toISOString().slice(0, 10);
    return `
      <form class="card stack" data-replace-meter-form data-meter-id="${esc(meter.id)}">
        <h3>${esc(t('property.meters.replace.title'))}</h3>
        <div class="muted">
          ${esc(t('property.meters.replace.explain'))}
        </div>
        <div class="field">
          <label>${esc(t('property.meters.replace.new_serial'))}</label>
          <input class="input" name="serial_number" placeholder="${esc(meter.serial_number || 'e.g. 998877')}" />
        </div>
        <div class="field">
          <label>${esc(t('property.meters.replace.new_label'))}</label>
          <input class="input" name="label" value="${esc(meter.label || '')}" />
        </div>
        <div class="row" style="gap:var(--space-3)">
          <div class="field" style="flex:1">
            <label>${esc(t('property.meters.replace.starting_reading', { uom: meter.unit_of_measure }))}</label>
            <input class="input" name="starting_value" type="number" step="0.001" min="0" inputmode="decimal" placeholder="${esc(t('property.meters.replace.starting_reading_placeholder'))}" />
          </div>
          <div class="field" style="max-width:180px">
            <label>${esc(t('property.meters.replace.reading_date'))}</label>
            <input class="input" name="starting_date" type="date" value="${esc(today)}" />
          </div>
        </div>
        <div class="field">
          <label>${esc(t('property.meters.replace.note'))}</label>
          <input class="input" name="starting_notes" placeholder="${esc(t('property.meters.replace.note_placeholder'))}" />
        </div>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="cancel-replace-meter">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary">${esc(t('property.meters.replace.submit'))}</button>
        </div>
      </form>
    `;
  }

  _unitRowHtml(u) {
    const meta = [esc(u.type)];
    if (u.size_sqm) meta.push(esc(u.size_sqm) + ' m²');
    if (u.asking_rent != null && u.asking_rent !== '') {
      const cur = (this.state.property?.currency || 'HUF');
      meta.push(esc(formatMoney(u.asking_rent, cur)) + esc(t('public.per_month_suffix')));
    }
    if (auth.marketplaceEnabled && Number(u.is_published) === 0) meta.push(`<span class="badge">${esc(t('property.unit_row.hidden_from_public'))}</span>`);
    return `
      <div class="list-item">
        ${icon(unitIcon(u.type), { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(u.identifier)}</strong>
          <span class="faint">${meta.join(' · ')}</span>
          ${this._availabilityHtml(u)}
        </div>
        <button class="btn btn-ghost btn-icon" data-action="edit-unit" data-unit-id="${esc(u.id)}" aria-label="${esc(t('property.edit_unit_aria'))}">${icon('pencil', { size: 'sm' })}</button>
      </div>
    `;
  }

  _availabilityHtml(u) {
    if (u.availability_status === 'vacant') {
      return `<span class="badge badge-success">${esc(t('property.unit_row.vacant'))}</span>`;
    }
    if (u.current_tenancy_open_ended) {
      return `<span class="badge badge-warning">${esc(t('property.unit_row.occupied_open_ended'))}</span>`;
    }
    if (u.current_tenancy_end_date) {
      return `<span class="badge badge-warning">${esc(t('property.unit_row.occupied_until', { date: u.current_tenancy_end_date }))}</span>`;
    }
    return '';
  }

  _unitFormHtml() {
    const currency = (this.state.property?.currency || 'HUF').toUpperCase();
    const count = Array.isArray(this.state.units) ? this.state.units.length : 0;
    return unitFormHtml({
      newUnit: this.state.newUnit,
      index: count + 1,
      currency,
      busy: this._unitBusy,
    });
  }

  mount() {
    const on = (sel, evt, fn) => this.el.querySelectorAll(sel).forEach((el) => el.addEventListener(evt, fn));

    on('[data-action="add-unit"]', 'click', () => this.setState({
      addingUnit: !this.state.addingUnit, editingUnitId: null, error: null,
      newUnit: emptyUnitDraft(),
    }));
    on('[data-action="edit-property"]', 'click', () => this.setState({ editingProperty: true, error: null }));
    on('[data-action="cancel-property"]', 'click', () => this.setState({ editingProperty: false, error: null }));
    on('[data-action="delete-property"]', 'click', () => this._deleteProperty());
    on('[data-action="restore-property"]', 'click', () => this._restoreProperty());
    on('[data-action="delete-property-permanent"]', 'click', () => this._deletePropertyPermanently());
    // v5 unit-card actions: expand opens the inline editor with a draft;
    // collapse drops the draft; save PUTs the draft; remove deletes.
    on('[data-action="expand-unit"]', 'click', (e) => this._expandUnit(e.currentTarget.getAttribute('data-id')));
    on('[data-action="collapse-unit"]', 'click', (e) => this._collapseUnit(e.currentTarget.getAttribute('data-id')));
    on('[data-action="save-unit-edit"]', 'click', (e) => this._saveUnitEdit(e.currentTarget.getAttribute('data-id')));
    on('[data-action="cancel-unit-edit"]', 'click', (e) => this._collapseUnit(e.currentTarget.getAttribute('data-id')));
    on('[data-action="remove-unit"]', 'click', (e) => this._deleteUnitById(e.currentTarget.getAttribute('data-id')));
    on('[data-action="cancel-unit-form"]', 'click', () => this.setState({
      addingUnit: false, newUnit: emptyUnitDraft(), error: null,
    }));
    on('[data-action="submit-unit-form"]', 'click', () => this._saveNewUnit());
    // Wire input tracking into the per-unit drafts and into newUnit, so
    // keystrokes don't re-render and lose focus.
    this.el.querySelectorAll('[data-unit-edit-form]').forEach((form) => {
      const id = form.getAttribute('data-unit-id');
      if (!id || !this.state.unitDrafts[id]) return;
      form.querySelectorAll('input,select,textarea').forEach((inp) => {
        const evt = (inp.type === 'checkbox' || inp.tagName === 'SELECT') ? 'change' : 'input';
        inp.addEventListener(evt, (e) => {
          const name = e.target.name;
          if (!name) return;
          if (e.target.type === 'checkbox') {
            this.state.unitDrafts[id][name] = e.target.checked;
          } else {
            this.state.unitDrafts[id][name] = e.target.value;
          }
        });
      });
      wireUnitFormApartmentOnly(form);
      // Country picker + HU postal-code autofill for the optional unit
      // address override. Blank country = inherit from property, so
      // requireDefault:false.
      wireAddressFields(form, { requireDefault: false });
      // Currency picker keeps the rent/közös suffix live; inherit
      // fallback is the property's default currency.
      wireUnitCurrencySuffix(form, (this.state.property?.currency || 'HUF'));
      // Per-unit furnished radio + energy certificate (energy_rating preset
      // and the OÉNY HET-code lookup don't fire plain input events, so they
      // need explicit wiring into the draft).
      wireFurnishedRadio(form, { onChange: (v) => { this.state.unitDrafts[id].furnished = v; } });
      wireEnergyRatingInputs(form, { onChange: (v) => { this.state.unitDrafts[id].energy_rating = v; } });
      wireEnergyCertVerify(form, {
        onMatch: (row) => {
          if (row?.expirationDate) this.state.unitDrafts[id].energy_certificate_expires_at = String(row.expirationDate).slice(0, 10);
          if (row?.energyClassification) this.state.unitDrafts[id].energy_rating = String(row.energyClassification).toUpperCase();
        },
      });
      // Amenities live on the unit now (migration 0084). Chips need an
      // explicit click handler to toggle chip-on; the draft mirrors the
      // selection so unitPayloadFromForm sees the latest list.
      wireAmenitiesChips(form, {
        initial: Array.isArray(this.state.unitDrafts[id].amenities) ? this.state.unitDrafts[id].amenities : [],
        onChange: (list) => { this.state.unitDrafts[id].amenities = list; },
      });
    });
    const newUnitForm = this.el.querySelector('[data-unit-form]');
    if (newUnitForm) {
      newUnitForm.querySelectorAll('input,select,textarea').forEach((inp) => {
        inp.addEventListener('input', (e) => {
          const name = e.target.name;
          if (name) this.state.newUnit[name] = e.target.value;
        });
      });
      wireUnitFormApartmentOnly(newUnitForm);
      wireAddressFields(newUnitForm, { requireDefault: false });
      wireUnitCurrencySuffix(newUnitForm, (this.state.property?.currency || 'HUF'));
      wireFurnishedRadio(newUnitForm, { onChange: (v) => { this.state.newUnit.furnished = v; } });
      wireEnergyRatingInputs(newUnitForm, { onChange: (v) => { this.state.newUnit.energy_rating = v; } });
      wireEnergyCertVerify(newUnitForm, {
        onMatch: (row) => {
          if (row?.expirationDate) this.state.newUnit.energy_certificate_expires_at = String(row.expirationDate).slice(0, 10);
          if (row?.energyClassification) this.state.newUnit.energy_rating = String(row.energyClassification).toUpperCase();
        },
      });
      wireAmenitiesChips(newUnitForm, {
        initial: Array.isArray(this.state.newUnit.amenities) ? this.state.newUnit.amenities : [],
        onChange: (list) => { this.state.newUnit.amenities = list; },
      });
    }

    on('[data-action="add-meter"]', 'click', (e) => this.setState({
      addingMeterUnitId: e.currentTarget.getAttribute('data-unit-id'),
      editingMeterId: null,
      error: null,
    }));
    on('[data-action="edit-meter"]', 'click', (e) => this.setState({
      editingMeterId: e.currentTarget.getAttribute('data-meter-id'),
      addingMeterUnitId: null,
      error: null,
    }));
    on('[data-action="cancel-meter"]', 'click', () => this.setState({
      addingMeterUnitId: null, editingMeterId: null, error: null,
    }));
    on('[data-action="delete-meter"]', 'click', () => this._deleteMeter());
    on('[data-action="open-replace-meter"]', 'click', (e) => this.setState({
      replacingMeterId: e.currentTarget.getAttribute('data-meter-id'),
      editingMeterId: null,
      addingMeterUnitId: null,
      error: null,
    }));
    on('[data-action="cancel-replace-meter"]', 'click', () => this.setState({
      replacingMeterId: null, error: null,
    }));
    this.el.querySelectorAll('[data-replace-meter-form]').forEach((f) => {
      f.addEventListener('submit', (e) => this._replaceMeter(e));
    });

    on('[data-action="add-reading"]', 'click', (e) => this.setState({
      addingReadingMeterId: e.currentTarget.getAttribute('data-meter-id'),
      editingReadingId: null,
      error: null,
    }));
    on('[data-action="edit-reading"]', 'click', (e) => this.setState({
      editingReadingId: e.currentTarget.getAttribute('data-reading-id'),
      addingReadingMeterId: null,
      error: null,
    }));
    on('[data-action="cancel-reading"]', 'click', () => this.setState({
      addingReadingMeterId: null, editingReadingId: null, error: null,
    }));
    on('[data-action="delete-reading"]', 'click', () => this._deleteReading());

    on('[data-action="toggle-publishing"]', 'click', () => this.setState({
      publishingOpen: !this.state.publishingOpen, error: null,
    }));
    on('[data-action="cancel-publishing"]', 'click', () => this.setState({
      publishingOpen: false, error: null,
    }));
    on('[data-action="copy-public-url"]', 'click', (e) => this._copyToClipboard(e.currentTarget.getAttribute('data-url')));
    const pubForm = this.el.querySelector('[data-publishing-form]');
    if (pubForm) pubForm.addEventListener('submit', (e) => this._savePublishing(e));

    const propForm = this.el.querySelector('[data-property-form]');
    if (propForm) {
      propForm.addEventListener('submit', (e) => this._saveProperty(e));
      // Energy + amenities both moved to the unit (edited on each unit
      // card); the property form keeps only currency / deposit now.
    }

    this.el.querySelectorAll('[data-meter-form]').forEach((f) => {
      f.addEventListener('submit', (e) => this._saveMeter(e));
      const kindSel = f.querySelector('[data-meter-kind]');
      if (kindSel) kindSel.addEventListener('change', () => this._toggleMeterFeeFields(f));
    });
    this.el.querySelectorAll('[data-reading-form]').forEach((f) =>
      f.addEventListener('submit', (e) => this._saveReading(e))
    );

    // Tab switching (in-place; updates the URL, no router remount).
    on('[data-tab]', 'click', (e) => this._setTab(e.currentTarget.getAttribute('data-tab')));

    if (this.state.property === null) this._load();

    // Mount only the active tab's panel(s). Tearing down the previous
    // generation first frees their poll timers / blob URLs.
    this._destroyUnitPanels();
    this._destroyTabPanels();
    if (this.state.property) {
      const isOwner = !!(auth.user && this.state.property.owner_user_id === auth.user.id);
      // Both views share one fetch, so either panel can refresh BOTH the
      // documents and photos badges.
      const onDocCount = (c) => {
        if (c && typeof c === 'object') {
          this._setTabCount('documents', c.documents);
          this._setTabCount('photos', c.photos);
        }
      };
      const tab = this.state.tab;
      if (tab === 'documents' && this.el.querySelector('[data-documents-slot]')) {
        this._tabPanels.push(attachDocumentsPanel(this.el, { propertyId: this.id, canManage: isOwner, view: 'documents', onCount: onDocCount }));
      } else if (tab === 'photos' && this.el.querySelector('[data-photos-slot]')) {
        this._tabPanels.push(attachDocumentsPanel(this.el, { propertyId: this.id, canManage: isOwner, view: 'photos', slot: '[data-photos-slot]', onCount: onDocCount }));
      } else if (tab === 'members' && this.el.querySelector('[data-members-slot]')) {
        this._tabPanels.push(attachMembersPanel(this.el, {
          entity: 'property', entityId: this.id, canManage: isOwner,
          landlordUserId: this.state.property.landlord_user_id || null,
          landlordExternalOwnerId: this.state.property.landlord_external_owner_id || null,
          ownerUserId: this.state.property.owner_user_id || null,
          onLandlordChange: (uid) => {
            this.state.property.landlord_user_id = uid;
            this.state.property.landlord_external_owner_id = null;
          },
          onCount: (n) => this._setTabCount('members', n),
        }));
      } else if (tab === 'units') {
        // Per-unit panels only mount for expanded cards (collapsed cards
        // don't render the slot divs).
        for (const u of (this.state.units || [])) {
          if (!this.state.unitDrafts[u.id]) continue;
          const meters = attachUnitMetersPanel(this.el, { unitId: u.id, canManage: isOwner });
          const docs = attachUnitDocumentsPanel(this.el, { unitId: u.id, canManage: isOwner });
          const owners = attachUnitExternalOwnersPanel(this.el, {
            unitId: u.id, canManage: isOwner,
            propertyId: this.id,
            landlordExternalOwnerId: this.state.property.landlord_external_owner_id || null,
            canDesignateIssuer: isOwner,
            onIssuerChange: (oid) => {
              this.state.property.landlord_external_owner_id = oid;
              this.state.property.landlord_user_id = null;
            },
          });
          if (meters || docs || owners) this._unitPanels[u.id] = { meters, docs, owners };
        }
      }
    }
  }

  destroy() {
    this._destroyTabPanels();
    this._destroyUnitPanels();
  }

  _destroyUnitPanels() {
    const ids = Object.keys(this._unitPanels || {});
    for (const id of ids) {
      const entry = this._unitPanels[id];
      try { entry?.meters?.destroy?.(); } catch {}
      try { entry?.docs?.destroy?.(); } catch {}
      try { entry?.owners?.destroy?.(); } catch {}
      delete this._unitPanels[id];
    }
  }

  // ---- v5 unit-card: expand / collapse / save / remove / add ----

  _expandUnit(id) {
    if (!id || this.state.unitDrafts[id]) return;
    const u = (this.state.units || []).find((x) => x.id === id);
    if (!u) return;
    this.setState({ unitDrafts: { ...this.state.unitDrafts, [id]: unitDraftFromRow(u) } });
  }

  _collapseUnit(id) {
    if (!id || !this.state.unitDrafts[id]) return;
    const next = { ...this.state.unitDrafts };
    delete next[id];
    this.setState({ unitDrafts: next });
  }

  async _saveUnitEdit(id) {
    const draft = this.state.unitDrafts[id];
    if (!draft || this._unitBusy) return;
    const form = this.el.querySelector(`[data-unit-edit-form][data-unit-id="${id}"]`);
    if (form) clearFormError(form);
    const identifier = (draft.identifier || '').trim();
    if (!identifier) {
      if (form) showFormError(form, t('property.wizard.errors.unit_identifier_required') || t('property.unit_form.identifier'), { field: 'identifier' });
      return;
    }
    const payload = { identifier, type: draft.type || 'apartment' };
    if (draft.helyrajzi_szam != null) {
      const hrsz = String(draft.helyrajzi_szam).trim();
      if (hrsz !== '') payload.helyrajzi_szam = hrsz;
    }
    const size = (draft.size_sqm || '').toString().trim();
    if (size !== '') payload.size_sqm = size;
    payload.room_count = (draft.room_count || '').toString().trim();
    payload.floor = (draft.floor || '').toString().trim();
    const rentMajor = parseFloat(String(draft.asking_rent || ''));
    payload.asking_rent = Number.isFinite(rentMajor) ? Math.round(rentMajor * 100) : null;
    const kozosMajor = parseFloat(String(draft.kozos_koltseg || ''));
    payload.kozos_koltseg = Number.isFinite(kozosMajor) ? Math.round(kozosMajor * 100) : null;
    if (draft.furnished) payload.furnished = draft.furnished;
    const maxOcc = parseInt(String(draft.max_occupants || ''), 10);
    payload.max_occupants = Number.isFinite(maxOcc) && maxOcc >= 0 ? maxOcc : null;
    payload.addr_street       = (draft.addr_street       || '').toString().trim();
    payload.addr_house_number = (draft.addr_house_number || '').toString().trim();
    payload.addr_building     = (draft.addr_building     || '').toString().trim();
    payload.addr_staircase    = (draft.addr_staircase    || '').toString().trim();
    payload.addr_door         = (draft.addr_door         || '').toString().trim();
    payload.city          = (draft.city          || '').toString().trim();
    payload.postal_code   = (draft.postal_code   || '').toString().trim();
    payload.country       = (draft.country       || '').toString().trim();
    payload.notes         = (draft.notes         || '').toString().trim();
    payload.energy_rating                 = (draft.energy_rating                 || '').toString().trim();
    payload.energy_certificate_number     = (draft.energy_certificate_number     || '').toString().trim();
    payload.energy_certificate_expires_at = (draft.energy_certificate_expires_at || '').toString().trim();
    if (draft.helyrajzi_szam != null && String(draft.helyrajzi_szam).trim() === '') payload.helyrajzi_szam = '';
    this._unitBusy = true;
    try {
      const updated = await api.put(`/units/${encodeURIComponent(id)}`, payload);
      const units = (this.state.units || []).map((x) => (x.id === id ? { ...x, ...updated } : x));
      const drafts = { ...this.state.unitDrafts };
      delete drafts[id];
      this._unitBusy = false;
      this.setState({ units, unitDrafts: drafts });
    } catch (err) {
      this._unitBusy = false;
      const msg = err instanceof ApiError ? err.message : t('property.errors.save');
      const field = err instanceof ApiError
        ? fieldFromApiError(err, ['identifier', 'type', 'size_sqm', 'asking_rent'])
        : null;
      if (form) showFormError(form, msg, field ? { field } : {});
    }
  }

  async _saveNewUnit() {
    if (this._unitBusy) return;
    const u = this.state.newUnit;
    const form = this.el.querySelector('[data-unit-form]');
    if (form) clearFormError(form);
    const identifier = (u.identifier || '').trim();
    if (!identifier) {
      if (form) showFormError(form, t('property.wizard.errors.unit_identifier_required') || t('property.unit_form.identifier'), { field: 'identifier' });
      return;
    }
    const payload = { identifier, type: u.type || 'apartment' };
    if (u.helyrajzi_szam != null) {
      const hrsz = String(u.helyrajzi_szam).trim();
      if (hrsz !== '') payload.helyrajzi_szam = hrsz;
    }
    const size = (u.size_sqm || '').toString().trim();
    if (size !== '') payload.size_sqm = size;
    const rooms = (u.room_count || '').toString().trim();
    if (rooms !== '') payload.room_count = rooms;
    const floor = (u.floor || '').toString().trim();
    if (floor !== '') payload.floor = floor;
    const rentMajor = parseFloat(String(u.asking_rent || ''));
    payload.asking_rent = Number.isFinite(rentMajor) ? Math.round(rentMajor * 100) : null;
    const kozosMajor = parseFloat(String(u.kozos_koltseg || ''));
    if (Number.isFinite(kozosMajor)) payload.kozos_koltseg = Math.round(kozosMajor * 100);
    if (u.furnished) payload.furnished = u.furnished;
    const maxOcc = parseInt(String(u.max_occupants || ''), 10);
    if (Number.isFinite(maxOcc) && maxOcc >= 0) payload.max_occupants = maxOcc;
    const astreet = (u.addr_street || '').toString().trim();
    const ahouse  = (u.addr_house_number || '').toString().trim();
    const abld    = (u.addr_building || '').toString().trim();
    const astair  = (u.addr_staircase || '').toString().trim();
    const adoor   = (u.addr_door || '').toString().trim();
    const ac = (u.city || '').toString().trim();
    const ap = (u.postal_code || '').toString().trim();
    const ax = (u.country || '').toString().trim();
    if (astreet) payload.addr_street = astreet;
    if (ahouse) payload.addr_house_number = ahouse;
    if (abld) payload.addr_building = abld;
    if (astair) payload.addr_staircase = astair;
    if (adoor) payload.addr_door = adoor;
    if (ac) payload.city = ac;
    if (ap) payload.postal_code = ap;
    if (ax) payload.country = ax;
    const notes = (u.notes || '').toString().trim();
    if (notes) payload.notes = notes;
    const erating = (u.energy_rating || '').toString().trim();
    const ecert = (u.energy_certificate_number || '').toString().trim();
    const eexp = (u.energy_certificate_expires_at || '').toString().trim();
    if (erating) payload.energy_rating = erating;
    if (ecert) payload.energy_certificate_number = ecert;
    if (eexp) payload.energy_certificate_expires_at = eexp;
    this._unitBusy = true;
    try {
      const row = await api.post(`/properties/${this.id}/units`, payload);
      const units = [...(this.state.units || []), row];
      this._unitBusy = false;
      // Auto-expand the freshly-created unit so meters and the rest of
      // basics are immediately reachable, matching the wizard's UX.
      this.setState({
        units,
        addingUnit: false,
        newUnit: emptyUnitDraft(),
        unitDrafts: { ...this.state.unitDrafts, [row.id]: unitDraftFromRow(row) },
      });
    } catch (err) {
      this._unitBusy = false;
      const msg = err instanceof ApiError ? err.message : t('property.errors.save');
      const field = err instanceof ApiError
        ? fieldFromApiError(err, ['identifier', 'type', 'size_sqm', 'asking_rent'])
        : null;
      if (form) showFormError(form, msg, field ? { field } : {});
    }
  }

  async _deleteUnitById(id) {
    if (!id) return;
    if (!confirm(t('property.wizard.units.delete_confirm') || t('common.confirm_delete') || 'Delete?')) return;
    try {
      await api.delete(`/units/${encodeURIComponent(id)}`);
      const drafts = { ...this.state.unitDrafts };
      delete drafts[id];
      this.setState({
        units: (this.state.units || []).filter((u) => u.id !== id),
        unitDrafts: drafts,
      });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.errors.save') });
    }
  }

  async _load() {
    try {
      const [property, units] = await Promise.all([
        api.get(`/properties/${this.id}`),
        api.get(`/properties/${this.id}/units`),
      ]);
      const unitList = units.data || [];
      this.setState({ property, units: unitList });
      this._loadMeters(unitList);
      this._loadTenancies();
      if (auth.hasFeature('maintenance')) this._loadMaintenance();
      this._loadTabCounts();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.errors.load') });
    }
  }

  // Tenancies for the Bérlők tab (+ its badge count). Loaded up front with
  // the rest so the count shows without opening the tab.
  async _loadTenancies() {
    try {
      const r = await api.get(`/properties/${this.id}/tenancies`);
      this.setState({ tenancies: (r && r.data) || [], tenanciesError: null });
      // Unread chat badges per tenancy row; failures stay silent.
      api.get('/messages/unread').then((u) => {
        if (u && u.by_tenancy && Object.keys(u.by_tenancy).length > 0) {
          this.setState({ tenancyUnread: u.by_tenancy });
        }
      }).catch(() => {});
    } catch (err) {
      this.setState({ tenanciesError: err instanceof ApiError ? err.message : t('tenancies.errors.load') });
    }
  }

  // Maintenance requests for the Karbantartás tab (+ its open-count badge).
  async _loadMaintenance() {
    try {
      const r = await api.get(`/properties/${this.id}/maintenance`);
      this.setState({ maintenance: (r && r.data) || [], maintenanceError: null });
    } catch (err) {
      this.setState({ maintenanceError: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.load') });
    }
  }

  // Seed the documents / photos / members badge counts up front so they show
  // without visiting each tab. The panels themselves keep the counts live via
  // onCount once their tab is opened. Best-effort — badges just stay blank on
  // failure. isImageMime mirrors DocumentsPanel's photo/doc split.
  async _loadTabCounts() {
    const isImg = (m) => typeof m === 'string' && m.startsWith('image/');
    try {
      const r = await api.get(`/properties/${this.id}/documents`);
      const rows = (r && r.data) || [];
      const photos = rows.filter((x) => x.kind === 'PHOTO' && isImg(x.mime)).length;
      this._setTabCount('photos', photos);
      this._setTabCount('documents', rows.length - photos);
    } catch { /* leave blank */ }
    try {
      const r = await api.get(`/properties/${this.id}/memberships`);
      const members = ((r && r.data) || []).length + ((r && Array.isArray(r.pending)) ? r.pending.length : 0);
      this._setTabCount('members', members);
    } catch { /* leave blank */ }
  }

  async _loadMeters(unitList) {
    if (!unitList.length) {
      this.setState({ meters: {}, readings: {} });
      return;
    }
    try {
      const results = await Promise.all(
        unitList.map((u) => api.get(`/units/${u.id}/meters`).then((r) => [u.id, r.data || []]))
      );
      const meters = Object.fromEntries(results);
      this.setState({ meters });
      const allMeters = results.flatMap(([, list]) => list);
      this._loadReadings(allMeters);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.meters.errors.load') });
    }
  }

  async _reloadMetersForUnit(unitId) {
    try {
      const r = await api.get(`/units/${unitId}/meters`);
      const list = r.data || [];
      this.setState({ meters: { ...this.state.meters, [unitId]: list } });
      this._loadReadings(list);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.meters.errors.load') });
    }
  }

  async _loadReadings(meterList) {
    if (!meterList.length) return;
    try {
      const results = await Promise.all(
        meterList.map((m) => api.get(`/meters/${m.id}/readings`).then((r) => [m.id, r.data || []]))
      );
      this.setState({ readings: { ...this.state.readings, ...Object.fromEntries(results) } });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.readings.errors.load') });
    }
  }

  async _reloadReadingsForMeter(meterId) {
    try {
      const r = await api.get(`/meters/${meterId}/readings`);
      this.setState({ readings: { ...this.state.readings, [meterId]: r.data || [] } });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.readings.errors.load') });
    }
  }

  async _saveProperty(e) {
    e.preventDefault();
    const form = e.target;
    clearFormError(form);
    const payload = collectBasicsPayload(form);
    try {
      const property = await api.put(`/properties/${this.id}`, payload);
      this.setState({ property, editingProperty: false, error: null });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('property.errors.save');
      const field = err instanceof ApiError ? fieldFromApiError(err, BASICS_API_FIELDS) : null;
      showFormError(form, message, { field });
    }
  }

  /**
   * Archive (soft-delete) the property. The DB row stays; the listing
   * filters it out via deleted_at IS NULL. Reversible via _restoreProperty.
   * Backed by DELETE /properties/{id} which the backend remapped to a
   * soft delete in PropertyService::delete.
   */
  async _deleteProperty() {
    if (!confirm(t('property.archive_confirm'))) return;
    try {
      await api.delete(`/properties/${this.id}`);
      window.location.hash = '#/properties';
    } catch (err) {
      const form = this.el.querySelector('[data-property-form]');
      const message = err instanceof ApiError ? err.message : t('property.errors.archive');
      if (form) showFormError(form, message);
      else this.setState({ error: message });
    }
  }

  async _restoreProperty() {
    try {
      const fresh = await api.post(`/properties/${this.id}/restore`, {});
      this.setState({ property: fresh, error: null });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('property.errors.restore');
      this.setState({ error: message });
    }
  }

  async _deletePropertyPermanently() {
    if (!confirm(t('property.delete_permanently_confirm'))) return;
    try {
      await api.delete(`/properties/${this.id}/permanent`);
      window.location.hash = '#/properties';
    } catch (err) {
      // Server returns 409 with reason=tenancy_usage when a unit is still
      // on a tenancy. Surface the message verbatim — it already tells the
      // user what to do (end and archive the blocking tenancies).
      const message = err instanceof ApiError ? err.message : t('property.errors.delete_permanently');
      this.setState({ error: message });
    }
  }

  async _saveUnit(e) {
    e.preventDefault();
    const form = e.target;
    clearFormError(form);
    const editingId = form.getAttribute('data-unit-id');
    const fd = new FormData(form);
    const pubInput = form.querySelector('input[name="is_published"]');
    const payload = {
      type: String(fd.get('type') || ''),
      identifier: String(fd.get('identifier') || ''),
      is_published: pubInput ? pubInput.checked : false,
    };
    const size = String(fd.get('size_sqm') || '');
    if (size !== '') payload.size_sqm = size;
    const rentMajor = parseFloat(String(fd.get('asking_rent') || ''));
    payload.asking_rent = Number.isFinite(rentMajor) ? Math.round(rentMajor * 100) : null;
    try {
      if (editingId) {
        await api.put(`/units/${editingId}`, payload);
      } else {
        await api.post(`/properties/${this.id}/units`, payload);
      }
      this.setState({ addingUnit: false, editingUnitId: null, units: null, error: null });
      this._load();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('property.unit_form.errors.save');
      const field = err instanceof ApiError
        ? fieldFromApiError(err, ['identifier', 'type', 'size_sqm', 'asking_rent'])
        : null;
      showFormError(form, message, { field });
    }
  }

  async _deleteUnit() {
    const id = this.state.editingUnitId;
    if (!id) return;
    if (!confirm(t('property.unit_form.delete_confirm'))) return;
    try {
      await api.delete(`/units/${id}`);
      this.setState({ editingUnitId: null, units: null, error: null });
      this._load();
    } catch (err) {
      const form = this.el.querySelector('[data-unit-form]');
      const message = err instanceof ApiError ? err.message : t('property.unit_form.errors.delete');
      if (form) showFormError(form, message);
      else this.setState({ error: message });
    }
  }

  async _savePublishing(e) {
    e.preventDefault();
    const form = e.target;
    clearFormError(form);
    const fd = new FormData(form);
    const payload = {
      is_published: form.querySelector('input[name="is_published"]').checked,
      public_title: String(fd.get('public_title') || '').trim(),
      public_description: String(fd.get('public_description') || '').trim(),
      public_contact_email: String(fd.get('public_contact_email') || '').trim(),
      public_contact_phone: String(fd.get('public_contact_phone') || '').trim(),
    };
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      const property = await api.patch(`/properties/${this.id}/publishing`, payload);
      this.setState({ property, publishingBusy: false, publishingOpen: false });
    } catch (err) {
      if (btn) btn.disabled = false;
      const message = err instanceof ApiError ? err.message : t('property.errors.publishing');
      const field = err instanceof ApiError
        ? fieldFromApiError(err, ['public_title', 'public_description', 'public_contact_email', 'public_contact_phone'])
        : null;
      showFormError(form, message, { field });
    }
  }

  async _copyToClipboard(text) {
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (_) { /* noop */ }
  }

  _toggleMeterFeeFields(form) {
    const kind = form.querySelector('[data-meter-kind]')?.value;
    const isFee = kind === 'fee';
    form.querySelector('[data-uom-field]')?.toggleAttribute('hidden', isFee);
    form.querySelector('[data-unit-price-field]')?.toggleAttribute('hidden', isFee);
    form.querySelector('[data-due-day-field]')?.toggleAttribute('hidden', isFee);
    form.querySelector('[data-starting-field]')?.toggleAttribute('hidden', isFee);
  }

  async _saveMeter(e) {
    e.preventDefault();
    const form = e.target;
    const unitId = form.getAttribute('data-unit-id');
    const editingId = form.getAttribute('data-meter-id');
    const payload = Object.fromEntries(new FormData(form).entries());
    for (const f of ['monthly_fee', 'unit_price']) {
      if (payload[f] === '' || payload[f] == null) {
        delete payload[f];
      } else {
        const n = parseFloat(payload[f]);
        payload[f] = Number.isFinite(n) ? Math.round(n * 100) : '';
      }
    }
    const isFee = payload.kind === 'fee';
    if (editingId || isFee || payload.starting_value === '' || payload.starting_value == null) {
      delete payload.starting_value;
      delete payload.starting_date;
      delete payload.starting_notes;
    } else {
      if (!payload.starting_date) delete payload.starting_date;
      if (!payload.starting_notes) delete payload.starting_notes;
    }
    try {
      if (editingId) {
        await api.put(`/meters/${editingId}`, payload);
      } else {
        await api.post(`/units/${unitId}/meters`, payload);
      }
      this.setState({ addingMeterUnitId: null, editingMeterId: null, error: null });
      this._reloadMetersForUnit(unitId);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.meters.errors.save') });
    }
  }

  async _deleteMeter() {
    const id = this.state.editingMeterId;
    if (!id) return;
    if (!confirm(t('property.meters.delete_confirm'))) return;
    const unitId = this._unitIdForMeter(id);
    try {
      await api.delete(`/meters/${id}`);
      this.setState({ editingMeterId: null, error: null });
      if (unitId) this._reloadMetersForUnit(unitId);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.meters.errors.delete') });
    }
  }

  async _replaceMeter(e) {
    e.preventDefault();
    const form = e.target;
    const id = form.getAttribute('data-meter-id');
    const unitId = this._unitIdForMeter(id);
    const raw = Object.fromEntries(new FormData(form).entries());
    const payload = {};
    if (raw.serial_number !== undefined && raw.serial_number !== '') payload.serial_number = raw.serial_number;
    if (raw.label !== undefined && raw.label !== '') payload.label = raw.label;
    if (raw.starting_value !== undefined && raw.starting_value !== '') {
      payload.starting_value = raw.starting_value;
      if (raw.starting_date) payload.starting_date = raw.starting_date;
      if (raw.starting_notes) payload.starting_notes = raw.starting_notes;
    }
    if (!confirm(t('property.meters.replace.confirm'))) return;
    try {
      await api.post(`/meters/${id}/replace`, payload);
      this.setState({ replacingMeterId: null, error: null });
      if (unitId) {
        this._reloadMetersForUnit(unitId);
      }
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.meters.errors.replace') });
    }
  }

  _unitIdForMeter(meterId) {
    for (const [uid, list] of Object.entries(this.state.meters)) {
      if ((list || []).some((m) => m.id === meterId)) return uid;
    }
    return null;
  }

  async _saveReading(e) {
    e.preventDefault();
    const form = e.target;
    const meterId = form.getAttribute('data-meter-id');
    const editingId = form.getAttribute('data-reading-id');
    const payload = Object.fromEntries(new FormData(form).entries());
    try {
      if (editingId) {
        await api.put(`/readings/${editingId}`, payload);
      } else {
        await api.post(`/meters/${meterId}/readings`, payload);
      }
      this.setState({ addingReadingMeterId: null, editingReadingId: null, error: null });
      this._reloadReadingsForMeter(meterId);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.readings.errors.save') });
    }
  }

  async _deleteReading(force = false) {
    const id = this.state.editingReadingId;
    if (!id) return;
    if (!force && !confirm(t('property.readings.delete_confirm'))) return;
    const meterId = this._meterIdForReading(id);
    try {
      const url = force ? `/readings/${id}?force=true` : `/readings/${id}`;
      await api.delete(url);
      this.setState({ editingReadingId: null, error: null });
      if (meterId) this._reloadReadingsForMeter(meterId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        if (confirm(t('property.readings.delete_proforma_confirm'))) {
          return this._deleteReading(true);
        }
        return;
      }
      this.setState({ error: err instanceof ApiError ? err.message : t('property.readings.errors.delete') });
    }
  }

  _meterIdForReading(readingId) {
    for (const [mid, list] of Object.entries(this.state.readings)) {
      if ((list || []).some((r) => r.id === readingId)) return mid;
    }
    return null;
  }
}
